import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gift, BookMarked, Sparkles, BookOpen, Feather, ArrowRight, Check } from "lucide-react";
import { CoffretGiftForm, emptyGift } from "@/components/CoffretGiftForm";
import { ReaderReviews } from "@/components/ReaderReviews";

const items = [
  { icon: BookMarked, name: "PDF « Les Fondements du Tao des 4C »", price: "9,90 €", extra: true },
  { icon: BookOpen, name: "L'Aventure", price: "19,90 €" },
  { icon: Sparkles, name: "Le Jeu", price: "9,90 €" },
  { icon: Feather, name: "1 carnet PDF de 21 jours au choix", price: "9,90 €" },
];

const COMPARE = [
  ["L'Aventure", true, true],
  ["Le Jeu — 21 cartes", true, true],
  ["1 carnet PDF de 21 jours au choix", true, true],
  ["PDF « Les Fondements du Tao des 4C »", false, true],
  ["Prix", "29,90 €", "34,90 €"],
];

export const CoffretHighlight = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [carnets, setCarnets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [forGift, setForGift] = useState(false);
  const [gift, setGift] = useState(emptyGift());

  useEffect(() => {
    Promise.all([api.get("/carnets"), api.get("/foundation").catch(() => ({ data: null }))]).then(([r, f]) => {
      const fid = f.data?.id;
      const list = r.data || [];
      setReady(list.some((c) => fid && c.book_id === fid && c.active_sale && c.has_pdf));
      setCarnets(list.filter((c) => c.visible && c.active_sale && (!fid || c.book_id !== fid)));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const startCheckout = async ({ carnet, gift: g }) => {
    setBusy(true);
    try {
      const body = g ? { product: "coffret_gift", carnet_id: carnet, gift: g } : { product: "coffret", carnet_id: carnet };
      const { data } = await api.post("/payments/checkout", { ...body, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "Déjà débloqué") toast.success("Coffret déjà débloqué");
      else toast.error(typeof d === "string" ? d : "Paiement impossible");
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!authed) return;
    const pending = sessionStorage.getItem("tao_coffret_pending");
    if (pending) {
      sessionStorage.removeItem("tao_coffret_pending");
      try { startCheckout(JSON.parse(pending)); } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const buy = () => {
    if (!selected) { toast.error("Choisissez d'abord votre carnet de 21 jours"); return; }
    if (forGift && (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(gift.recipient_email.trim()) || !gift.sender_name.trim())) {
      toast.error("Indiquez l'e-mail de votre proche et votre prénom");
      return;
    }
    const order = { carnet: selected, gift: forGift ? gift : null };
    sessionStorage.setItem("tao_unlock_book", forGift ? "gift" : "jeu-ile-de-labondance");
    if (!authed) {
      sessionStorage.setItem("tao_coffret_pending", JSON.stringify(order));
      navigate(`/connexion?next=${window.location.pathname}`);
      return;
    }
    startCheckout(order);
  };

  const available = ready && carnets.length > 0;

  return (
    <section data-testid="coffret-section" className="bg-[#1F2A24] text-[#F6F2E9]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-16 md:pb-24">
        <div className="rounded-3xl border-2 border-[#C9A24B] bg-gradient-to-br from-[#C9A24B]/20 to-transparent p-8 md:p-12 shadow-[0_30px_80px_-40px_rgba(201,162,75,0.6)]">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#F6F2E9] text-[#1F2A24] px-4 py-1.5 font-ui text-xs tracking-[0.2em] uppercase font-semibold">
              <Gift size={14} /> L'offre la plus complète
            </span>
            <h2 className="font-serif text-3xl md:text-5xl mt-5" data-testid="coffret-title">Le Coffret Découverte du Tao des 4C</h2>
            <p className="font-serif italic text-lg text-[#F6F2E9]/80 mt-3">Tout le Pack 3 Univers + le livre fondateur en PDF</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="coffret-items">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.name} className={`relative rounded-2xl border p-6 text-center ${it.extra ? "border-[#C9A24B] bg-[#C9A24B]/10" : "border-[#C9A24B]/20 bg-white/5"}`}>
                  {it.extra && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-3 py-0.5 font-ui text-[10px] tracking-[0.15em] uppercase">En plus</span>}
                  <span className="w-11 h-11 mx-auto rounded-full flex items-center justify-center bg-[#C9A24B]/20 text-[#C9A24B]"><Icon size={20} /></span>
                  <p className="font-serif text-base mt-4 leading-snug">{it.name}</p>
                  <p className="font-sans text-sm text-[#F6F2E9]/50 mt-1 line-through">{it.price}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-5 mt-10">
            <div className="text-center">
              <p className="font-sans text-sm text-[#F6F2E9]/50 line-through" data-testid="coffret-value">Valeur totale 49,60 €</p>
              <p className="font-serif text-5xl text-[#C9A24B] leading-none mt-1" data-testid="coffret-price">34,90 €</p>
            </div>
            <span className="rounded-full bg-[#A63B36] text-white px-4 py-1.5 font-ui text-sm tracking-wide" data-testid="coffret-saving">Vous économisez 14,70 €</span>
          </div>

          <CompareTable />

          <div className="mt-10 max-w-md mx-auto">
            <div className="grid grid-cols-2 gap-1 rounded-full bg-white/10 p-1 mb-5" data-testid="coffret-mode">
              {[[false, "Pour moi"], [true, "À offrir"]].map(([v, l]) => (
                <button key={l} onClick={() => setForGift(v)} data-testid={v ? "coffret-mode-gift" : "coffret-mode-self"}
                  className={`rounded-full py-2 font-ui text-sm transition-colors inline-flex items-center justify-center gap-1.5 ${forGift === v ? "bg-[#C9A24B] text-[#1F2A24]" : "text-[#F6F2E9]/70 hover:text-white"}`}>
                  {v && <Gift size={14} />} {l}
                </button>
              ))}
            </div>
            <label htmlFor="coffret-carnet" className="block font-ui text-sm text-[#F6F2E9]/80 mb-2">{forGift ? "Choisir le carnet de 21 jours à offrir" : "Choisir mon carnet de 21 jours"}</label>
            <select id="coffret-carnet" data-testid="coffret-carnet-select" value={selected} onChange={(e) => setSelected(e.target.value)} disabled={!available}
              className="w-full rounded-full bg-white/10 border border-[#C9A24B]/40 text-[#F6F2E9] px-5 py-3 font-ui text-sm focus:outline-none focus:border-[#C9A24B] disabled:opacity-50">
              <option value="" className="text-[#1F2A24]">{!loaded ? "Chargement…" : available ? "— Sélectionnez un carnet —" : "Coffret bientôt disponible"}</option>
              {carnets.map((c) => (
                <option key={c.id} value={c.id} className="text-[#1F2A24]">{`Carnet ${c.number ? c.number + " — " : ""}${c.title || c.name || ""}`}</option>
              ))}
            </select>
            {forGift && <CoffretGiftForm gift={gift} setGift={setGift} />}
            <button data-testid="coffret-buy-btn" onClick={buy} disabled={busy || !available}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[#b8923f] transition-colors disabled:opacity-50">
              {busy ? "Redirection…" : <>{forGift ? "Offrir le coffret" : "Obtenir le coffret"} <span className="whitespace-nowrap">— 34,90 €</span></>} <ArrowRight size={18} />
            </button>
            <p className="text-center font-sans text-xs text-[#F6F2E9]/50 mt-3" data-testid="coffret-note">{forGift
              ? "Votre proche reçoit votre message et l'accès au coffret à la date choisie. Vous recevez un reçu dès le paiement."
              : "Accès immédiat aux Fondements, à l'Aventure, au Jeu et à votre carnet PDF après paiement."}</p>
          </div>
        </div>
        <ReaderReviews />
      </div>
    </section>
  );
};

const Cell = ({ v }) => (v === true ? <Check size={16} className="mx-auto text-[#C9A24B]" /> : v === false ? <span className="text-[#F6F2E9]/30">—</span> : <span className="font-serif text-lg text-[#C9A24B]">{v}</span>);

const CompareTable = () => (
  <div className="mt-10 max-w-xl mx-auto overflow-x-auto rounded-2xl border border-[#C9A24B]/25" data-testid="offers-compare">
    <table className="w-full text-xs sm:text-sm">
      <thead className="bg-white/5 font-ui text-xs tracking-wide text-[#F6F2E9]/70">
        <tr><th className="text-left px-4 py-3">Comparez les offres</th><th className="px-3 py-3">Pack 3 Univers</th><th className="px-3 py-3 text-[#C9A24B]">Coffret Découverte</th></tr>
      </thead>
      <tbody className="font-sans">
        {COMPARE.map(([label, a, b]) => (
          <tr key={label} className="border-t border-[#C9A24B]/15"><td className="px-4 py-2.5 text-[#F6F2E9]/85">{label}</td><td className="text-center"><Cell v={a} /></td><td className="text-center bg-[#C9A24B]/5"><Cell v={b} /></td></tr>
        ))}
      </tbody>
    </table>
  </div>
);
